import { round } from "lodash";
import React, { useRef, useMemo } from "react";
import { cn } from "@/shared/utils/shadcn";
import { areaColorsMap, ScoreChartProps } from ".";
import { Score } from "@/shared/services/api/graphql/generated";
import { PersonScore } from "@/features/people/hooks/useDimensionsWithScores";

interface ScoreChartCompactProps extends ScoreChartProps {
  score?: Score;
  scoreMultiplier?: number; // to handle the case where the score is not in the range of 0-100
}

const HEIGT_ADJUSTMENT = 20; // Adjust for the average score text

const ScoreChartCompact: React.FC<ScoreChartCompactProps> = (props) => {
  const {
    scores,
    renderScore,
    showArea = false,
    areaColor,
    averageScore,
    width = 125,
    height = 24,
    score,
    scoreMultiplier = 1,
  } = props;
  const containerRef = useRef<HTMLDivElement>(null);

  const { minScore, maxScore } = useMemo(() => {
    const values = scores.map((score) => score.value || 0);
    const min = values.length > 0 ? Math.min(...values) : 0;
    const max = values.length > 0 ? Math.max(...values) : 0;
    return {
      minScore: isFinite(min) ? min : 0,
      maxScore: isFinite(max) ? max : 0,
    };
  }, [scores]);

  const left = `${score?.value ? score.value * scoreMultiplier : 0}%`;

  return (
    <div
      ref={containerRef}
      className={cn("w-full relative overflow-x-visible")}
    >
      <svg
        width={width + 10}
        height={height + HEIGT_ADJUSTMENT} // Add extra space for the average score text
        viewBox={`0 0 ${width + 10} ${height + HEIGT_ADJUSTMENT}`}
        preserveAspectRatio="none"
        className="absolute -top-[20px] left-0"
      >
        {showArea && areaColor && (
          <rect
            x={((minScore || 0) / 100) * width}
            y={HEIGT_ADJUSTMENT + 2} // Start below the average score text
            width={((maxScore - minScore) / 100) * width}
            height={height - 4}
            fill={areaColorsMap[areaColor].bg}
          />
        )}

        {/* Horizontal line */}
        <line
          x1={0}
          y1={height / 2 + HEIGT_ADJUSTMENT} // Adjust for the average score text
          x2={width}
          y2={height / 2 + HEIGT_ADJUSTMENT} // Adjust for the average score text
          stroke="#D1D5DB"
          strokeWidth="1"
        />
        {/* Vertical line at the center */}
        <line
          x1={width / 2}
          y1={HEIGT_ADJUSTMENT}
          x2={width / 2}
          y2={height + HEIGT_ADJUSTMENT}
          stroke="#D1D5DB"
          strokeWidth="1"
        />
        {/* Area and end lines */}

        {/* Average score text */}
        {!!averageScore && (
          <>
            <line
              x1={(averageScore / 100) * width}
              y1={HEIGT_ADJUSTMENT} // Start below the average score text
              x2={(averageScore / 100) * width}
              y2={height + HEIGT_ADJUSTMENT} // Extend to the bottom of the chart
              stroke={areaColor ? areaColorsMap[areaColor].stroke : "#000000"}
              strokeWidth="2"
            />
            <text
              x={(averageScore / 100) * width}
              y={15} // Position above the chart
              textAnchor="middle"
              fill={areaColor ? areaColorsMap[areaColor].stroke : "#000000"}
              fontSize="12"
              fontWeight="600"
            >
              {round(averageScore)}
            </text>
          </>
        )}
      </svg>
      {score && (
        <div className="relative" style={{ height, width }}>
          <div
            key={`${score.obj_id}-${score.obj_type}`}
            className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2"
            style={{ left }}
          >
            {renderScore(score as PersonScore)}
          </div>
        </div>
      )}
    </div>
  );
};

export default ScoreChartCompact;
